import { useNavigation } from '@remix-run/react'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Layer, LayerProps, Source, useMap } from 'react-map-gl'
import { searchParcels } from '~/api'
import {
  aDrawMode,
  aDrawnFeature,
  aParcelDataResidentialVisible,
  aParcelDataVisible,
  useAtomValue,
} from '~/atoms'
import { MapView, useBreakpoint, useCurrentMapView } from '~/hooks'
import type { ParcelProperties } from '~/models'
import { REGRID_TOKEN } from '~/utils'
import { MapMobilePopup, MapParcelPopup } from '../parcels'

type Coordinates = {
  lat: number
  lon: number
}

type ParcelClickEvent = (mapboxgl.MapMouseEvent | mapboxgl.MapTouchEvent) & {
  features?: mapboxgl.MapboxGeoJSONFeature[] | undefined
} & mapboxgl.EventData

export default function MapParcelsLayer() {
  const { mdDown } = useBreakpoint()
  const navigation = useNavigation()
  const parcelDataVisible = useAtomValue(aParcelDataVisible)
  const parcelDataResidentialVisible = useAtomValue(
    aParcelDataResidentialVisible
  )
  const currentMapView = useCurrentMapView()
  const mapRef = useMap()
  const drawMode = useAtomValue(aDrawMode)
  const drawFeature = useAtomValue(aDrawnFeature)

  const [regridSourceData, setRegridSourceData] = useState<any>(null)
  const [shouldInitLayer, setShouldInitLayer] = useState(false)
  const [selectedParcel, setSelectedParcel] = useState<ParcelProperties | null>(
    null
  )
  const [selectedParcelId, setSelectedParcelId] = useState<string | null>(null)
  const [selectedParcelIndex, setSelectedParcelIndex] = useState<number | null>(
    null
  )
  const [popupCoordinates, setPopupCoordinates] = useState<Coordinates>({
    lat: 0,
    lon: 0,
  })

  const initRegridSource = useCallback(async () => {
    const response = await fetch(
      `https://tiles.regrid.com/api/v1/sources?token=${REGRID_TOKEN}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: { parcel: true },
          fields: {
            // 'usecode', 'usedesc', 'parval', 'landval',
            parcel: ['ll_uuid', 'zoning_type'],
          },
        }),
      }
    )
    const result = await response.json()
    setRegridSourceData(result)
  }, [])

  useEffect(() => {
    initRegridSource()
    setTimeout(() => {
      setShouldInitLayer(true)
    }, 500)
  }, [])

  const parcelQuery = useQuery({
    queryKey: ['parcel', 'disable-cache', selectedParcel?.ll_uuid],
    queryFn: async () => {
      const parcels = await searchParcels(
        popupCoordinates.lat,
        popupCoordinates.lon,
        100
      )
      return parcels
    },
    enabled: !!selectedParcel?.ll_uuid,
  })

  const parcels: ParcelProperties[] = useMemo(() => {
    if ((parcelQuery?.data?.features || []).length > 0) {
      setSelectedParcelIndex(0)

      return parcelQuery.data!.features.map((feature: any) => {
        return feature.properties as ParcelProperties
      })
    }

    return []
  }, [parcelQuery.data])

  const selectedParcelData = useMemo(() => {
    if (selectedParcelIndex === null) return null
    return parcels[selectedParcelIndex] ?? null
  }, [parcels, selectedParcelIndex])

  const onPopupClose = useCallback(() => {
    if (mapRef.current && selectedParcelId && navigation.state !== 'loading') {
      mapRef.current?.setFeatureState(
        {
          source: 'parcel-data',
          sourceLayer: regridSourceData.id,
          id: selectedParcelId,
        },
        { selected: false }
      )
    }
    setSelectedParcel(null)
  }, [mapRef, selectedParcelId, regridSourceData, navigation])

  useEffect(() => {
    if (mapRef.current && !drawMode && regridSourceData) {
      const map = mapRef.current?.getMap()
      let hoveredPolygonId: string | null = null
      let isListingClicked = false
      if (!map) {
        return
      }

      let touchStartTime: number | null = null
      let touchStartPosition: { x: number; y: number } | null = null

      function onMouseMove(e: ParcelClickEvent) {
        isListingClicked = false

        if ((e.features || []).length > 0) {
          if (hoveredPolygonId !== null) {
            mapRef.current!.getMap().getCanvasContainer().style.cursor = 'grab'
            map.setFeatureState(
              {
                source: 'parcel-data',
                sourceLayer: regridSourceData.id,
                id: hoveredPolygonId,
              },
              { hover: false }
            )
          }
          hoveredPolygonId = e.features![0].id as string
          mapRef.current!.getMap().getCanvasContainer().style.cursor = 'pointer'
          map.setFeatureState(
            {
              source: 'parcel-data',
              sourceLayer: regridSourceData.id,
              id: hoveredPolygonId,
            },
            { hover: true }
          )
        }
      }

      function onParcelClick(e: ParcelClickEvent) {
        if (!isListingClicked && (e.features || []).length > 0) {
          const clickedFeature = e.features![0]
          const clickedId = clickedFeature.id as string

          setPopupCoordinates({
            lat: e.lngLat.lat,
            lon: e.lngLat.lng,
          })

          setSelectedParcel(clickedFeature.properties as ParcelProperties)

          if (selectedParcelId) {
            map.setFeatureState(
              {
                source: 'parcel-data',
                sourceLayer: regridSourceData.id,
                id: selectedParcelId,
              },
              { selected: false }
            )
          }

          setSelectedParcelId(clickedId)

          map.setFeatureState(
            {
              source: 'parcel-data',
              sourceLayer: regridSourceData.id,
              id: clickedId,
            },
            { selected: true }
          )
        }
        isListingClicked = false
      }

      function onTouchStart(e: mapboxgl.MapTouchEvent) {
        touchStartTime = Date.now()
        touchStartPosition = { x: e.point.x, y: e.point.y }
      }

      function onTouchEnd(e: ParcelClickEvent) {
        if (touchStartTime === null || touchStartPosition === null) return

        const touchEndTime = Date.now()
        const touchEndPosition = { x: e.point.x, y: e.point.y }

        const touchDistanceX = Math.abs(
          touchStartPosition.x - touchEndPosition.x
        )
        const touchDistanceY = Math.abs(
          touchStartPosition.y - touchEndPosition.y
        )
        const touchDuration = touchEndTime - touchStartTime

        const maxDistance = 10
        const maxDuration = 300

        const isClick =
          touchDuration < maxDuration &&
          touchDistanceX < maxDistance &&
          touchDistanceY < maxDistance

        touchStartTime = null
        touchStartPosition = null

        if (isClick) {
          onParcelClick(e)
        } else {
          onMouseMove(e)
        }

        if (hoveredPolygonId !== null) {
          map.setFeatureState(
            {
              source: 'parcel-data',
              sourceLayer: regridSourceData.id,
              id: hoveredPolygonId,
            },
            { hover: false }
          )
        }
      }

      const onPropertyClick = (e: ParcelClickEvent) => {
        if (e.features && e.features.length > 0) {
          isListingClicked = true
          setSelectedParcel(null)
        } else {
          isListingClicked = false
        }
      }

      map.on('click', 'property-single-clusters', onPropertyClick)
      map.on('touchstart', 'property-single-clusters', onPropertyClick)
      map.on('click', 'parcels-fills', onParcelClick)
      map.on('touchstart', 'parcels-fills', onTouchStart)
      map.on('touchend', 'parcels-fills', onTouchEnd)
      map.on('mousemove', 'parcels-fills', onMouseMove)

      map.on('mouseleave', 'parcels-fills', () => {
        isListingClicked = false
        if (hoveredPolygonId !== null) {
          mapRef.current!.getMap().getCanvasContainer().style.cursor = 'grab'
          map.setFeatureState(
            {
              source: 'parcel-data',
              sourceLayer: regridSourceData.id,
              id: hoveredPolygonId,
            },
            { hover: false }
          )
        }
        hoveredPolygonId = null
      })
    }
  }, [mapRef.current, selectedParcelId, regridSourceData, drawMode])

  const layerStyle: LayerProps = {
    id: 'parcels',
    'source-layer': regridSourceData?.id,
    filter: ['!=', ['get', 'zoning_type'], 'Residential'],
    type: 'line',
    beforeId: 'property-clusters',
    interactive: true,
    paint: {
      'line-color': '#000',
      'line-opacity': ['match', ['get', 'zoning_type'], 'Residential', 0, 0.15],
    },
    layout: {},
  }

  const layerStyleFill = useMemo(() => {
    const style: LayerProps = {
      id: 'parcels-fills',
      type: 'fill',
      'source-layer': regridSourceData?.id,
      beforeId: 'property-clusters',
      layout: {},
      paint: {
        'fill-color': [
          'case',
          ['boolean', ['feature-state', 'hover'], false],
          '#7c3aed',
          '#000000',
        ],
        'fill-opacity': [
          'case',
          ['boolean', ['feature-state', 'hover'], false],
          0.25,
          0.1,
        ],
      },
    }
    if (parcelDataResidentialVisible === false) {
      style.filter = ['!=', ['get', 'zoning_type'], 'Residential']
    }
    return style
  }, [parcelDataResidentialVisible, regridSourceData])

  if (
    !parcelDataVisible ||
    currentMapView !== MapView.Map ||
    !!drawMode ||
    !!drawFeature ||
    !shouldInitLayer ||
    regridSourceData === null
  ) {
    return null
  }

  return (
    <>
      <Source
        id="parcel-data"
        type="vector"
        tiles={regridSourceData.vector}
        promoteId="ll_uuid"
        minzoom={13}>
        <Layer {...layerStyle} />
        <Layer {...layerStyleFill} />
      </Source>
      {selectedParcel && (
        <MapMobilePopup
          className="show-close !m-0 w-full select-text !p-0"
          mdDown={mdDown}
          maxWidth="400px"
          key={selectedParcel.ll_uuid}
          latitude={popupCoordinates.lat}
          longitude={popupCoordinates.lon}
          closeButton={true}
          closeOnMove={true}
          closeOnClick={false}
          onClose={onPopupClose}
          style={{ zIndex: 48 }}>
          <MapParcelPopup
            parcels={parcels}
            selectedParcelData={selectedParcelData}
            selectedParcelIndex={selectedParcelIndex}
            setSelectedParcelIndex={setSelectedParcelIndex}
          />
        </MapMobilePopup>
      )}
    </>
  )
}
